"use client";

import React from "react";
import { Target, Mail, ChartBar, PenTool, Code, Fingerprint, Megaphone, Shield, TriangleAlert, ArrowRight, Terminal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const categories = [{
  id: "marketing",
  label: "Web & SEO"
}, {
  id: "creative",
  label: "Growth & Content"
}, {
  id: "pr",
  label: "Strategy & Support"
}];
const capabilities = [{
  id: "cap1",
  categoryId: "marketing",
  title: "WordPress Design",
  description: "Premium, high-performance WordPress sites built for speed and conversion-driven user experiences.",
  iconName: "Code",
  link: "/ServicesPage"
}, {
  id: "cap2",
  categoryId: "marketing",
  title: "SEO Optimization",
  description: "Data-backed strategies to climb search rankings and capture high-intent organic traffic.",
  iconName: "Target",
  link: "/ServicesPage"
}, {
  id: "cap3",
  categoryId: "marketing",
  title: "Google Business",
  description: "Dominating local search results through expert profile management and reputation signals.",
  iconName: "ChartBar",
  link: "/ServicesPage"
}, {
  id: "cap4",
  categoryId: "creative",
  title: "Social Media Growth",
  description: "Strategic channel management designed to foster community and amplify your brand's reach.",
  iconName: "Megaphone",
  link: "/ServicesPage"
}, {
  id: "cap5",
  categoryId: "creative",
  title: "Content Creation",
  description: "High-impact visual and written assets that communicate your value and engage your audience.",
  iconName: "PenTool",
  link: "/ServicesPage"
}, {
  id: "cap6",
  categoryId: "creative",
  title: "Brand Identity",
  description: "Cohesive visual systems including logos and style guides that define your professional presence.",
  iconName: "Fingerprint",
  link: "/ServicesPage"
}, {
  id: "cap7",
  categoryId: "pr",
  title: "Client Training",
  description: "Empowering your team with the technical knowledge to manage and grow your digital assets.",
  iconName: "Mail",
  link: "/ServicesPage"
}, {
  id: "cap8",
  categoryId: "pr",
  title: "Strategic Consulting",
  description: "Long-term partnership focused on aligning digital growth with your core business objectives.",
  iconName: "Shield",
  link: "/ServicesPage"
}, {
  id: "cap9",
  categoryId: "pr",
  title: "Maintenance",
  description: "Proactive monitoring and technical updates to ensure your digital ecosystem remains secure.",
  iconName: "TriangleAlert",
  link: "/ServicesPage"
}];
const iconMap: Record<string, React.ElementType> = {
  Target,
  Mail,
  ChartBar,
  PenTool,
  Code,
  Fingerprint,
  Megaphone,
  Shield,
  TriangleAlert
};
export default function TheCapabilityGrid() {
  return <section data-section-id="75191" id="core-digital-services" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 z-0">
        {}
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.8%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E")'
      }} />
        {}
        <div className="absolute -top-[20%] -left-[10%] h-[500px] w-[500px] rounded-full bg-primary/20 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] h-[400px] w-[400px] rounded-full bg-primary/10 blur-[100px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 flex flex-col items-center text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-xs font-medium text-primary font-mono uppercase tracking-widest">
            <Terminal className="size-3" />
            <span>Service Modules</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl font-serif text-foreground">
            Core Digital Services
          </h2>
          <p className="mt-4 max-w-[700px] text-muted-foreground md:text-lg font-sans">
            Strategic digital solutions engineered to scale your brand. Our integrated ecosystem focuses on building lasting relationships through measurable growth and technical excellence.
          </p>
        </div>

        {}
        <div className="hidden md:block">
          <Tabs defaultValue={categories[0].id} className="w-full flex flex-col items-center">
            <TabsList className="mb-12 bg-card/40 backdrop-blur-xl border border-border/30 p-1.5 rounded-xl">
              {categories.map((category, i) => <TabsTrigger key={category.id} value={category.id} data-index={i} className="px-10 py-3.5 text-sm font-semibold tracking-wide data-[state=active]:bg-primary/20 data-[state=active]:text-primary data-[state=active]:border-primary/40 transition-all duration-300 font-serif">
                  {category.label}
                </TabsTrigger>)}
            </TabsList>

            {categories.map((category, i) => <TabsContent key={category.id} value={category.id} data-index={i} className="w-full mt-0 outline-none focus-visible:ring-0">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {capabilities.filter(cap => cap.categoryId === category.id).map((capability, j) => {
                const Icon = iconMap[capability.iconName];
                return <Card key={capability.id} data-index={j} className="group relative overflow-hidden bg-card/30 backdrop-blur-2xl border-border/20 hover:border-primary/40 transition-all duration-500 hover:shadow-[0_0_40px_-10px_rgba(var(--primary),0.2)] flex flex-col h-full rounded-2xl">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <CardHeader className="relative z-10 pb-4">
                            <div className="mb-4 inline-flex size-14 items-center justify-center rounded-xl bg-background/60 border border-border/30 text-primary shadow-lg group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-500">
                              {Icon && <Icon className="size-7" />}
                            </div>
                            <CardTitle className="text-2xl font-bold tracking-tight font-serif text-foreground">
                              {capability.title}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="relative z-10 flex-grow">
                            <CardDescription className="text-base text-muted-foreground leading-relaxed font-sans">
                              {capability.description}
                            </CardDescription>
                          </CardContent>
                          <CardFooter className="relative z-10 pt-4 border-t border-border/10 mt-auto">
                            <Link to={capability.link} className="w-full">
                              <Button variant="ghost" className="w-full justify-between group/btn hover:bg-primary/15 hover:text-primary transition-colors py-6">
                                <span className="font-mono text-xs uppercase tracking-widest font-bold">Deep Dive</span>
                                <ArrowRight className="size-4 transition-transform duration-300 group-hover/btn:translate-x-2" />
                              </Button>
                            </Link>
                          </CardFooter>
                        </Card>;
              })}
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {}
        <div className="md:hidden">
          <Accordion type="single" collapsible className="w-full space-y-4" defaultValue={categories[0].id}>
            {categories.map((category, i) => <AccordionItem key={category.id} value={category.id} data-index={i} className="border border-border/30 rounded-2xl bg-card/30 backdrop-blur-xl overflow-hidden data-[state=open]:border-primary/40 transition-colors">
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-card/50 data-[state=open]:bg-primary/10 transition-colors">
                  <span className="text-lg font-bold font-serif tracking-tight">{category.label}</span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-6 pt-2">
                  <div className="flex flex-col gap-4">
                    {capabilities.filter(cap => cap.categoryId === category.id).map((capability, j) => {
                  const Icon = iconMap[capability.iconName];
                  return <Card key={capability.id} data-index={j} className="bg-background/40 border-border/20 rounded-xl">
                            <CardHeader className="pb-2 flex flex-row items-center gap-4 space-y-0">
                              <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary border border-primary/20">
                                {Icon && <Icon className="size-5" />}
                              </div>
                              <CardTitle className="text-lg font-serif font-bold">{capability.title}</CardTitle>
                            </CardHeader>
                            <CardContent className="pb-4">
                              <CardDescription className="text-sm font-sans text-muted-foreground/90">
                                {capability.description}
                              </CardDescription>
                            </CardContent>
                            <CardFooter className="pt-0">
                              <Link to={capability.link} className="w-full">
                                <Button variant="outline" size="sm" className="w-full border-border/40 bg-background/40 hover:bg-primary/10 hover:text-primary transition-all">
                                  <span className="font-mono text-[10px] uppercase tracking-widest font-bold">Deep Dive</span>
                                  <ArrowRight className="ml-2 size-3" />
                                </Button>
                              </Link>
                            </CardFooter>
                          </Card>;
                })}
                  </div>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}